import { useEffect, useLayoutEffect, useRef } from "react";
import { useEditor, useValue } from "tldraw";
import { TldrawAgent } from "../../agent/TldrawAgent";
import { useAgents } from "../../agent/TldrawAgentAppProvider";
import type { AgentCursorState } from "../../agent/managers/AgentCursorManager";

/**
 * Renders cursor overlays for all agents.
 */
export function AllAgentCursors() {
  const agents = useAgents();

  return (
    <>
      {agents.map((agent) => (
        <AgentCursorOverlay key={agent.id} agent={agent} />
      ))}
    </>
  );
}

/**
 * Renders a cursor overlay for a single agent showing position and state.
 * Rendered as a plain div inside tldraw's Overlays container (which handles
 * camera transforms). Uses rAF lerping for smooth page-space movement.
 *
 * Position must not flow through React, and movement must use `transform`
 * rather than `top`/`left` — either would put per-action re-renders or
 * per-frame layout on the same thread as the tutor's audio.
 */
function AgentCursorOverlay({ agent }: { agent: TldrawAgent }) {
  const editor = useEditor();

  const visible = useValue("cursorVisible", () => agent.cursor.isVisible(), [
    agent,
  ]);
  const cursorState = useValue("cursorState", () => agent.cursor.getState(), [
    agent,
  ]);
  const zoom = useValue("zoom", () => editor.getZoomLevel(), [editor]);

  const containerRef = useRef<HTMLDivElement>(null);
  const currentPos = useRef<{ x: number; y: number } | null>(null);
  const rafId = useRef<number>(0);

  // Before paint, or it flashes at the origin: the loop's first frame is after.
  useLayoutEffect(() => {
    if (!visible) return;
    const el = containerRef.current;
    const target = agent.cursor.getPositionUntracked();
    if (!el || !target) return;
    currentPos.current = { x: target.x, y: target.y };
    el.style.transform = `translate3d(${target.x}px, ${target.y}px, 0)`;
  }, [visible, agent]);

  useEffect(() => {
    if (!visible) {
      cancelAnimationFrame(rafId.current);
      return;
    }

    const LERP_FACTOR = 0.08;

    function tick() {
      const el = containerRef.current;
      const tgt = agent.cursor.getPositionUntracked();
      if (!el || !tgt) {
        rafId.current = requestAnimationFrame(tick);
        return;
      }
      // A target that arrived while hidden has nothing to ease from; snap.
      const cur = (currentPos.current ??= { x: tgt.x, y: tgt.y });

      const dx = tgt.x - cur.x;
      const dy = tgt.y - cur.y;

      if (Math.abs(dx) < 0.1 && Math.abs(dy) < 0.1) {
        cur.x = tgt.x;
        cur.y = tgt.y;
      } else {
        cur.x += dx * LERP_FACTOR;
        cur.y += dy * LERP_FACTOR;
      }

      el.style.transform = `translate3d(${cur.x}px, ${cur.y}px, 0)`;

      rafId.current = requestAnimationFrame(tick);
    }

    rafId.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafId.current);
  }, [visible, agent]);

  // Reset current pos when hidden so next show snaps to new position
  useEffect(() => {
    if (!visible) {
      currentPos.current = null;
    }
  }, [visible]);

  if (!visible) return null;

  const isHiding = cursorState === "idle";
  const isThinking = cursorState === "thinking";
  const isListening = cursorState === "listening";
  const label = getCursorLabel(cursorState);

  const scale = 1 / zoom;
  const cursorSize = 20 * scale;

  return (
    <div
      ref={containerRef}
      className={`agent-cursor-wrapper ${isHiding ? "agent-cursor-hiding" : ""} ${isThinking ? "agent-cursor-thinking" : ""} ${isListening ? "agent-cursor-listening" : ""}`}
      style={{
        top: 0,
        left: 0,
      }}
    >
      {/* Cursor arrow — Figma-style pointer */}
      <svg
        width={20 * scale}
        height={24 * scale}
        viewBox="0 0 20 24"
        fill="none"
        style={{
          display: "block",
          filter: `drop-shadow(${1 * scale}px ${2 * scale}px ${3 * scale}px rgba(0,0,0,0.2))`,
        }}
      >
        <path
          d="M3 1L17.5 13.5H9.6L6.4 21.5C6 22.5 4.5 22.3 4.3 21.2L3 1Z"
          fill="#2D7A4F"
          strokeLinejoin="round"
        />
      </svg>

      {/* State label */}
      {label && (
        <div
          className="agent-cursor-label-pill"
          style={{
            marginTop: 2 * scale,
            marginLeft: cursorSize * 0.55,
            fontSize: `${14 * scale}px`,
            padding: `${6 * scale}px ${14 * scale}px`,
            borderRadius: `${999 * scale}px`,
          }}
        >
          {label}
        </div>
      )}
    </div>
  );
}

function getCursorLabel(state: AgentCursorState): string | null {
  switch (state) {
    case "listening":
      return "Listening\u2026";
    case "thinking":
      return "Thinking\u2026";
    case "creating":
      return "Creating\u2026";
    case "drawing":
      return "Drawing\u2026";
    case "editing":
      return "Editing\u2026";
    case "deleting":
      return "Deleting\u2026";
    case "moving":
      return "Moving\u2026";
    case "generating":
      return "Generating\u2026";
    case "idle":
      return null;
  }
}
